"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Pagination = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _customPropTypes = _interopRequireDefault(require("../../util/custom-prop-types"));
var _index = require("../Icon/index");
var _PaginationButton = require("../PaginationButton/PaginationButton");
var _PaginationJumpItem = require("../PaginationJumpItem/PaginationJumpItem");
var _PaginationList = require("../PaginationList/PaginationList");
var _PaginationNumberItem = require("../PaginationNumberItem/PaginationNumberItem");
var _PaginationReadoutItem = require("../PaginationReadoutItem/PaginationReadoutItem");
var _Text = require("../Text/Text");
var _pagination = require("./pagination.getIndexRange");
var _Pagination = require("./Pagination.styles");
var _excluded = ["currentPageIndex", "defaultCurrentPageIndex", "onPageSelected", "renderNextButton", "renderNumberButton", "renderPreviousButton", "totalPages"];
/**
 * @subcomponents ['PaginationButton']
 * @since 7.42.0
 */
var Pagination = function Pagination(props) {
  var currentPageIndex = props.currentPageIndex,
    defaultCurrentPageIndex = props.defaultCurrentPageIndex,
    onPageSelected = props.onPageSelected,
    renderNextButton = props.renderNextButton,
    renderNumberButton = props.renderNumberButton,
    renderPreviousButton = props.renderPreviousButton,
    totalPages = props.totalPages,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var _useState = (0, _react.useState)(defaultCurrentPageIndex),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    currentState = _useState2[0],
    setCurrentState = _useState2[1];

  // If a `currentPageIndex` has been specified, this renders as a controlled component
  var isControlled = typeof currentPageIndex !== 'undefined';
  var selectedPageIndex = isControlled ? currentPageIndex : currentState;
  var prevPageIndex = Math.max(selectedPageIndex - 1, 0);
  var nextPageIndex = Math.min(selectedPageIndex + 1, totalPages - 1);
  var buttonState = {
    currentPageIndex: selectedPageIndex,
    totalPages: totalPages
  };
  var handlePageSelected = (0, _react.useCallback)(function (index) {
    if (!isControlled) {
      setCurrentState(index);
    }
    if (onPageSelected) {
      onPageSelected(index);
    }
  }, [isControlled, onPageSelected]);
  var defaultPreviousButtonProps = {
    pageIndex: prevPageIndex,
    rel: 'prev',
    title: 'Previous page',
    disabled: selectedPageIndex === 0,
    onClick: function onClick() {
      return handlePageSelected(prevPageIndex);
    },
    children: /*#__PURE__*/_react.default.createElement(_index.IconChevronLeft, {
      size: "xs"
    })
  };
  var defaultNextButtonProps = {
    pageIndex: nextPageIndex,
    rel: 'next',
    title: 'Next page',
    disabled: selectedPageIndex === totalPages - 1,
    onClick: function onClick() {
      return handlePageSelected(nextPageIndex);
    },
    children: /*#__PURE__*/_react.default.createElement(_index.IconChevronRight, {
      size: "xs"
    })
  };
  var controls = [/*#__PURE__*/_react.default.createElement(_PaginationJumpItem.PaginationJumpItem, {
    key: "previous"
  }, renderPreviousButton(defaultPreviousButtonProps, buttonState))];
  var indexRange = (0, _pagination.getIndexRange)(selectedPageIndex, props.totalPages);
  indexRange.forEach(function (pageIndex) {
    var isSelected = pageIndex === selectedPageIndex;
    var defaultPageNumberButtonProps = {
      pageIndex: pageIndex,
      title: "Page " + (pageIndex + 1) + (isSelected ? ', current page' : ''),
      pressed: isSelected,
      disabled: isSelected,
      onClick: function onClick() {
        return handlePageSelected(pageIndex);
      },
      children: pageIndex + 1
    };

    /**
     * CDS-1071: Buttons need static keys to preserve focus location when selecting a page
     * CDS-537: The active button needs a persistent key to prevent flickering
     * when pressing next/previous
     */
    controls.push( /*#__PURE__*/_react.default.createElement(_PaginationNumberItem.PaginationNumberItem, {
      key: isSelected ? "selectedPageButton" : "page-" + pageIndex,
      "aria-current": isSelected ? 'page' : null
    }, renderNumberButton(defaultPageNumberButtonProps, buttonState)));
  });
  controls.push( /*#__PURE__*/_react.default.createElement(_PaginationReadoutItem.PaginationReadoutItem, {
    "aria-current": "page",
    key: "current"
  }, /*#__PURE__*/_react.default.createElement(_Text.Text, {
    fontType: "body",
    key: "selectedPage"
  }, "Page ", selectedPageIndex + 1, " of ", totalPages)));
  controls.push( /*#__PURE__*/_react.default.createElement(_PaginationJumpItem.PaginationJumpItem, {
    key: "next"
  }, renderNextButton(defaultNextButtonProps, buttonState)));
  return /*#__PURE__*/_react.default.createElement(_Pagination.StyledPagination, (0, _extends2.default)({
    role: "navigation",
    "aria-label": "" + props['aria-label']
  }, rest), /*#__PURE__*/_react.default.createElement(_PaginationList.PaginationList, null, controls));
};
exports.Pagination = Pagination;
Pagination.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * (Controlled component) The index of the currently-selected page.
   * Including this prop will render this as a controlled component.
   * To render an uncontrolled component, use `defaultCurrentPageIndex` instead.
   */
  'currentPageIndex': _propTypes.default.number,
  /**
   * (Uncontrolled component) The index of the currently-selected page.
   * Including this prop will render this as an uncontrolled component.
   * To render a controlled component, use `currentPageIndex` instead.
   */
  'defaultCurrentPageIndex': _propTypes.default.number,
  /**
   * The total number of available pages
   * */
  'totalPages': _propTypes.default.number.isRequired,
  /**
   * An event handler to attach to page selection events. Takes one argument:
   *     `pageIndex`: the newly selected page index
   */
  'onPageSelected': _propTypes.default.func,
  /**
   * A render function for the page number buttons. Takes two arguments:
   *      `props`: `PaginationButton` props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderNumberButton': _propTypes.default.func,
  /**
   * A render function for the "previous page" button. Takes two arguments:
   *      `props`: `PaginationButton` props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderPreviousButton': _propTypes.default.func,
  /**
   * A render function for the "next page" button. Takes two arguments:
   *      `props`: `PaginationButton` props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderNextButton': _propTypes.default.func,
  /**
   * A unique label that describes this component's purpose.
   * Especially necessary when using multiple pagination components on the same page.
   * */
  'aria-label': _propTypes.default.string,
  /**
   * When true, the wider version of the pagination component will be rendered,
   * providing numbered buttons that link directly to pages.
   *
   * Supports inline media query objects.
   */
  'showNumberButtons': _customPropTypes.default.inlineMediaQuery(_propTypes.default.bool),
  /**
   * @deprecated Use inline media query objects
   */
  'mediaQuery': _customPropTypes.default.mediaQuery
} : {};
Pagination.defaultProps = {
  'renderNumberButton': function renderNumberButton(props) {
    return /*#__PURE__*/_react.default.createElement(_PaginationButton.PaginationButton, props);
  },
  'renderPreviousButton': function renderPreviousButton(props) {
    return /*#__PURE__*/_react.default.createElement(_PaginationButton.PaginationButton, props);
  },
  'renderNextButton': function renderNextButton(props) {
    return /*#__PURE__*/_react.default.createElement(_PaginationButton.PaginationButton, props);
  },
  'onPageSelected': null,
  'aria-label': 'Pagination',
  'showNumberButtons': {
    xs: false,
    md: true
  }
};